import { useCallback, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Button } from '../../../ds/components';
import { useStageOptions } from '../api';
import { shortSum } from '../format';
import type {
  BoardCard as BoardCardData,
  BoardColumn as BoardColumnData,
  BoardResponse,
  StageOption,
} from '../types';
import { BoardCard, CardBody, type CardActions } from './BoardCard';
import styles from './board.module.css';

/**
 * Deal-Pipeline 01–16: the kanban of the property pipeline. Columns are the stages the server returns;
 * the late stages („Spätphasen", from „Beurkundet") are one collapsed group until expanded. Drag & drop
 * with @dnd-kit: while a card is dragged the target columns show the verdict of `stage-options`
 * (allowed → accent, locked → danger with the short text); the drop opens the stage dialog with the
 * target — the dialog is the binding check.
 */

/** Cards shown per column before „+ N weitere". */
const CARD_CAP = 6;
/** The seeded stage whose card menu records the commission receipt (Deal-Pipeline 22). */
const RECEIPT_STAGE_NAME = 'Provisionseingang';

export interface BoardEmptyTexts {
  /** Text of an empty column („leer" · „kein Deal im Filter" · „alles in der Frist" · „kein Treffer"). */
  column: string;
}

export interface PipelineBoardProps {
  data: BoardResponse;
  expanded: boolean;
  /** Deal-Pipeline 05: the cards carry their deadline line instead of the unit. */
  showDeadline: boolean;
  emptyTexts: BoardEmptyTexts;
  actions: CardActions;
  /** A card was dropped on another stage: open the stage dialog with that target. */
  onDrop: (card: BoardCardData, stageId: string) => void;
  /** A row of the late group was clicked: expand the board. */
  onExpandLate: () => void;
  selected: ReadonlySet<string>;
  onSelectionChange: (next: Set<string>) => void;
  onBulkStage: () => void;
  /** Deal-Pipeline 16: the cancelled column is shown after the stages. */
  cancelledColumn: BoardResponse['cancelledColumn'];
  /** Mobile (< 768): one list grouped by stage. */
  mobile: boolean;
  dimmed?: boolean | undefined;
}

interface DropVerdict {
  kind: 'allowed' | 'locked' | 'source';
  label: string;
  sub: string | null;
}

function verdictFor(
  option: StageOption | undefined,
  currentStageId: string,
  stageId: string,
): DropVerdict | null {
  if (stageId === currentStageId) return { kind: 'source', label: '', sub: null };
  if (!option) return null;
  if (option.kind === 'backward') {
    return { kind: 'allowed', label: 'Rückwärts erlaubt', sub: option.dropHint ?? 'Grund wird verlangt' };
  }
  if (option.allowed) {
    const warning = option.requirements.some((requirement) => requirement.state === 'warning');
    return {
      kind: 'allowed',
      label: warning ? 'Ablegen mit Warnung' : 'Ablegen erlaubt',
      sub: option.dropHint ?? (warning ? 'Grund wird verlangt' : 'Geprüft wird beim Ablegen'),
    };
  }
  const missing = option.requirements.find((requirement) => requirement.state === 'missing');
  return {
    kind: 'locked',
    label: missing ? `Ablegen prüft ${missing.label}` : 'Ablegen gesperrt',
    sub: option.dropHint ?? 'Sperre',
  };
}

interface ColumnProps {
  column: BoardColumnData;
  expanded: boolean;
  showDeadline: boolean;
  emptyText: string;
  actions: CardActions;
  permissions: BoardResponse['permissions'];
  selected: ReadonlySet<string>;
  onToggleSelect: ((card: BoardCardData) => void) | null;
  draggingId: string | null;
  verdict: DropVerdict | null;
  cancelled?: boolean | undefined;
}

function Column({
  column,
  expanded,
  showDeadline,
  emptyText,
  actions,
  permissions,
  selected,
  onToggleSelect,
  draggingId,
  verdict,
  cancelled = false,
}: ColumnProps) {
  const [showAll, setShowAll] = useState(false);
  const { setNodeRef, isOver } = useDroppable({ id: column.stageId, disabled: cancelled });
  const cards = showAll ? column.cards : column.cards.slice(0, CARD_CAP);
  // „+ N weitere" counts against the server's total of the stage, not only the cards delivered
  const more = showAll ? 0 : Math.max(column.count - cards.length, 0);
  const dragging = draggingId !== null;
  const dimmed = dragging && verdict === null && !cancelled;
  const writable = permissions.canChangeStage && !permissions.readOnly;

  return (
    <div
      ref={setNodeRef}
      role="listitem"
      aria-label={`${column.name} · ${column.count}`}
      className={clsx(styles.column, expanded && styles.columnExpanded, dimmed && styles.columnDimmed)}
    >
      <div className={styles.columnHead}>
        <span className={clsx(styles.columnName, cancelled && styles.columnNameDanger)}>{column.name}</span>
        <span className={clsx(styles.columnCount, column.count === 0 && styles.columnCountZero)}>
          {formatInteger(column.count)}
        </span>
      </div>
      <div
        className={clsx(
          styles.columnSub,
          (verdict?.kind === 'source' || (verdict?.kind === 'allowed' && isOver)) && styles.columnSubAccent,
          verdict?.kind === 'locked' && isOver && styles.columnSubDanger,
          (verdict?.kind === 'locked' || cancelled) && styles.columnSubDanger,
        )}
      >
        <span>{shortSum(column.valueCents)}</span>
        <span className={clsx(styles.columnOwner, cancelled && styles.columnOwnerDanger)}>
          {column.responsibleLabel ?? ''}
        </span>
      </div>

      {verdict && verdict.kind !== 'source' ? (
        <div
          className={clsx(
            styles.drop,
            verdict.kind === 'allowed' && styles.dropAllowed,
            verdict.kind === 'locked' && styles.dropLocked,
          )}
          aria-hidden="true"
        >
          <div className={styles.dropLabel}>{verdict.label}</div>
          {verdict.sub ? <div className={styles.dropSub}>{verdict.sub}</div> : null}
        </div>
      ) : null}

      {cards.map((card) => (
        <BoardCard
          key={card.id}
          card={card}
          showDeadline={showDeadline}
          actions={actions}
          draggable={writable && card.canDrag && !cancelled && onToggleSelect !== null}
          readOnly={permissions.readOnly}
          canChangeStage={writable && !cancelled}
          cancelled={cancelled}
          receiptStage={column.name === RECEIPT_STAGE_NAME}
          selecting={selected.size > 0}
          selected={selected.has(card.id)}
          onToggleSelect={cancelled ? null : onToggleSelect}
          isDragging={draggingId === card.id}
        />
      ))}
      {column.cards.length === 0 && !verdict ? (
        <div className={clsx(styles.columnEmpty, expanded && styles.columnEmptyTall)}>{emptyText}</div>
      ) : null}
      {more > 0 ? (
        <button type="button" className={styles.columnMore} onClick={() => setShowAll(true)}>
          + {formatInteger(more)} weitere
        </button>
      ) : null}
    </div>
  );
}

function LateGroup({ columns, onExpand }: { columns: BoardColumnData[]; onExpand: () => void }) {
  const count = columns.reduce((sum, column) => sum + column.count, 0);
  const masked = columns.some((column) => column.valueCents === null);
  const sum = masked ? null : columns.reduce((total, column) => total + (column.valueCents ?? 0), 0);
  return (
    <div
      role="listitem"
      aria-label={`Spätphasen · ${count}`}
      className={clsx(styles.column, styles.columnLate)}
    >
      <div className={styles.columnHead}>
        <span className={styles.columnName}>Spätphasen</span>
        <span className={clsx(styles.columnCount, count === 0 && styles.columnCountZero)}>
          {formatInteger(count)}
        </span>
      </div>
      <div className={styles.columnSub}>
        <span>{shortSum(sum)}</span>
        <span className={styles.columnOwner}>Backoffice</span>
      </div>
      <div className={styles.lateBox}>
        {columns.map((column) => (
          <button key={column.stageId} type="button" className={styles.lateRow} onClick={onExpand}>
            <span className={styles.lateName}>{column.name}</span>
            <span className={styles.lateCount}>{formatInteger(column.count)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function PipelineBoard({
  data,
  expanded,
  showDeadline,
  emptyTexts,
  actions,
  onDrop,
  onExpandLate,
  selected,
  onSelectionChange,
  onBulkStage,
  cancelledColumn,
  mobile,
  dimmed = false,
}: PipelineBoardProps) {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const options = useStageOptions(draggingId, draggingId !== null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space'] },
    }),
  );

  const allCards = useMemo(() => data.columns.flatMap((column) => column.cards), [data.columns]);
  const draggingCard = allCards.find((card) => card.id === draggingId) ?? null;
  const sourceStageId = useMemo(
    () => data.columns.find((column) => column.cards.some((card) => card.id === draggingId))?.stageId ?? null,
    [data.columns, draggingId],
  );

  const writable = data.permissions.canChangeStage && !data.permissions.readOnly;
  const toggleSelect = useCallback(
    (card: BoardCardData) => {
      const next = new Set(selected);
      if (next.has(card.id)) next.delete(card.id);
      else next.add(card.id);
      onSelectionChange(next);
    },
    [onSelectionChange, selected],
  );

  function verdictOf(stageId: string): DropVerdict | null {
    if (draggingId === null || sourceStageId === null) return null;
    const option = options.data?.options.find((entry) => entry.stageId === stageId);
    return verdictFor(option, sourceStageId, stageId);
  }

  function onDragStart(event: DragStartEvent) {
    setDraggingId(String(event.active.id));
  }

  function onDragEnd(event: DragEndEvent) {
    const card = draggingCard;
    setDraggingId(null);
    if (!card || !event.over) return;
    const stageId = String(event.over.id);
    if (stageId === sourceStageId) return;
    const option = options.data?.options.find((entry) => entry.stageId === stageId);
    if (option && option.kind === 'same') return;
    onDrop(card, stageId);
  }

  const visible = expanded ? data.columns : data.columns.filter((column) => !column.late);
  const late = data.columns.filter((column) => column.late);
  const selectedCards = allCards.filter((card) => selected.has(card.id));
  const selectedSum = selectedCards.some((card) => card.valueCents === null)
    ? null
    : selectedCards.reduce((sum, card) => sum + (card.valueCents ?? 0), 0);

  if (mobile) {
    return (
      <div className={styles.mobileGroups} role="list" aria-label="Pipeline">
        {data.columns
          .filter((column) => column.cards.length > 0 || !column.late)
          .map((column) => (
            <div key={column.stageId} role="listitem" aria-label={`${column.name} · ${column.count}`}>
              <div className={styles.mobileGroupHead}>
                <span className={styles.columnName}>{column.name}</span>
                <span className={styles.columnSub} style={{ borderBottom: 0, padding: 0 }}>
                  {shortSum(column.valueCents)}
                </span>
                <span className={clsx(styles.columnCount, column.count === 0 && styles.columnCountZero)}>
                  {formatInteger(column.count)}
                </span>
              </div>
              <div className={styles.mobileCards}>
                {column.cards.map((card) => (
                  <BoardCard
                    key={card.id}
                    card={card}
                    showDeadline={showDeadline}
                    actions={actions}
                    draggable={false}
                    readOnly={data.permissions.readOnly}
                    canChangeStage={writable}
                    receiptStage={column.name === RECEIPT_STAGE_NAME}
                    selecting={false}
                    selected={false}
                    onToggleSelect={null}
                    isDragging={false}
                  />
                ))}
                {column.cards.length === 0 ? (
                  <div className={styles.columnEmpty}>{emptyTexts.column}</div>
                ) : null}
              </div>
            </div>
          ))}
      </div>
    );
  }

  return (
    <>
      {selected.size > 0 ? (
        <div className={styles.selectionBar} role="region" aria-label="Auswahl">
          <span className={styles.selectionText}>
            {selected.size === 1 ? '1 Deal gewählt' : `${formatInteger(selected.size)} Deals gewählt`}
            {selectedSum !== null && selectedSum > 0 ? ` · ${shortSum(selectedSum)} €` : ''}
          </span>
          <span className={styles.selectionActions}>
            {writable ? (
              <Button variant="ghostInverse" size="small" onClick={onBulkStage}>
                Stage wechseln
              </Button>
            ) : null}
            <Button variant="ghostInverse" size="small" onClick={() => onSelectionChange(new Set())}>
              Auswahl aufheben
            </Button>
          </span>
        </div>
      ) : null}
      {draggingCard ? (
        <div className={styles.columnHead} style={{ padding: 0 }} aria-live="polite">
          <span className={styles.dragPill}>{draggingCard.contactName} wird verschoben</span>
          <span className={styles.escHint}>Esc bricht ab</span>
        </div>
      ) : null}
      <DndContext
        sensors={sensors}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={() => setDraggingId(null)}
      >
        <div className={clsx(styles.board, dimmed && styles.boardDimmed)} role="list" aria-label="Pipeline">
          {visible.map((column) => (
            <Column
              key={column.stageId}
              column={column}
              expanded={expanded}
              showDeadline={showDeadline}
              emptyText={emptyTexts.column}
              actions={actions}
              permissions={data.permissions}
              selected={selected}
              onToggleSelect={writable ? toggleSelect : null}
              draggingId={draggingId}
              verdict={verdictOf(column.stageId)}
            />
          ))}
          {!expanded && late.length > 0 ? <LateGroup columns={late} onExpand={onExpandLate} /> : null}
          {cancelledColumn ? (
            <Column
              column={{
                stageId: 'cancelled',
                name: 'Storniert · nur Admin',
                systemKey: null,
                sortOrder: 999,
                count: cancelledColumn.count,
                valueCents: cancelledColumn.cards.some((card) => card.valueCents === null)
                  ? null
                  : cancelledColumn.cards.reduce((sum, card) => sum + (card.valueCents ?? 0), 0),
                responsibleLabel: 'Backoffice',
                deadlineDays: null,
                late: false,
                cards: cancelledColumn.cards,
              }}
              expanded={expanded}
              showDeadline={false}
              emptyText="keine stornierten Deals"
              actions={actions}
              permissions={data.permissions}
              selected={selected}
              onToggleSelect={null}
              draggingId={draggingId}
              verdict={null}
              cancelled
            />
          ) : null}
        </div>
        <DragOverlay dropAnimation={null}>
          {draggingCard ? (
            <div className={clsx(styles.cardOverlay, expanded && styles.cardOverlayCompact)}>
              <CardBody card={draggingCard} showDeadline={showDeadline} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
}
